import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta, productLd, faqLd, softwareApiLd } from '../seo/Meta.jsx';
import { useLenis } from '../anim/useLenis.js';
import { MagneticLink } from '../anim/MagneticLink.jsx';
import { HeroLedger } from '../heroes/HeroLedger.jsx';

/**
 * Landing  -  institution-first pitch.
 *
 * Order of folds (each must justify the next scroll):
 *   1. Hero ticker   -  proves the product exists + is live
 *   2. Dual cards    -  engineer card + procurement card, same plan price
 *   3. Coverage      -  21 flag pills (proof of breadth)
 *   4. Anchor        -  category-leading commercial 1,250/mo struck, us 999/mo
 *   5. Trust grid    -  four hard differentiators
 *   6. FAQ           -  handles the four real institutional buying questions
 *   7. Final CTA     -  third framing of the same primary action
 *
 * Marketing tactics applied: specific numbers everywhere, anchored
 * pricing against the category list-price, risk-reversal copy ("cancel
 * anytime", "no card to view samples"), single primary action repeated
 * three times with different framings, FAQ as objection handler. Named
 * competitors are deliberately avoided in the rendered copy.
 */

const FAQ_ITEMS = [
  {
    q: 'Why $999 / month per institution and not per seat?',
    a: 'Procurement intelligence is a department-wide signal. Pricing per seat punishes the buyer for adding analysts to the work; per-institution pricing makes the decision once, at the legal entity. One key, one bill, every department member uses the same data.',
  },
  {
    q: 'Is the data licensed for resale and downstream products?',
    a: 'Yes. Every byte sourced under the explicit PSI / open-data licence of each jurisdiction. Per-country licence briefs are in the docs; an attribution string travels with every record. No redistribution risk.',
  },
  {
    q: 'How does this compare to TED\'s own API?',
    a: 'TED only carries ≥ €140k notices and only the EU-wide subset. We add 20 national portals plus sub-threshold awards (German Vergabe, Italian ANAC, Greek KIMDIS)  -  the long tail TED legally cannot publish.',
  },
  {
    q: 'What happens if we exceed the 500k records pool?',
    a: 'Same per-record rate as the included pool. No surprise step-up, no annual contract. The customer portal shows live usage; rate-limit yourself, upgrade tiers, or accept overage anytime.',
  },
  {
    q: 'Can we self-host or get a one-time bulk dump?',
    a: 'Sovereign tier supports on-prem mirrors, per-region data residency, and quarterly bulk exports. Institution tier is hosted-only  -  Stripe charges per active billing window. Solo / Institution can request a one-shot 30-day historical export at sign-up.',
  },
];

export default function Landing() {
  useLenis();

  return (
    <>
      <Meta
        title="tender-api — every public tender, one JSON feed"
        description="Public-procurement portals across the EU, UK, US, Switzerland, Ukraine and more — normalised into one JSON feed. Minute-level freshness, SSE-native, regulator-grade sources only. Per-institution licensing at $999 / month."
        path="/"
        jsonLd={[
          softwareApiLd(),
          productLd({
            name: 'tender-api — global procurement intelligence API',
            description:
              'Per-institution licensing for global procurement-notice intelligence. Government portals normalised; minute-level freshness.',
            offers: [{ name: 'Institution', price: 999 }],
          }),
          faqLd(FAQ_ITEMS),
        ]}
      />
      <Header />
      <main>
        <HeroLedger />
        <DualAudience />
        <CoverageStrip />
        <AnchorPrice />
        <TrustGrid />
        <ObjectionFAQ items={FAQ_ITEMS} />
        <FinalCTA />
      </main>
      <Footer />
    </>
  );
}

/* =====================================================================
 * Dual-audience block  -  engineer card + procurement card.
 * Same plan, two languages.
 * ================================================================== */
function DualAudience() {
  return (
    <section className="section section-dual">
      <div className="container">
        <div className="dual-grid">
          <motion.article
            className="dual-card dual-card--dev"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-15%' }}
            transition={{ duration: 0.7, ease: [0.7, 0, 0.15, 1] }}
          >
            <div className="dual-card-eyebrow mono">for engineers</div>
            <h3 className="dual-card-title">
              Stop maintaining a fleet of portal scrapers.
            </h3>
            <ul className="dual-card-list">
              <li>One auth header. One JSON shape across every jurisdiction.</li>
              <li>SSE stream + signed webhook + cursor-paginated REST.</li>
              <li>Verbatim upstream payload on a sibling endpoint when you need raw bytes.</li>
              <li>HMAC-SHA256 webhook signing, replay window, idempotency keys.</li>
            </ul>
            <pre className="dual-card-code">
{`# pull 25 newest IT tenders > $100k in DE/FR/NL
curl -H "X-Api-Key: \${OMNI_KEY}" \\
  "https://tender-api.com/v1/notices?\\
country=DE,FR,NL&cpv=72000000&\\
min_value=100000&limit=25"`}
            </pre>
            <Link to="/docs" className="dual-card-foot">
              Read the quickstart →
            </Link>
          </motion.article>

          <motion.article
            className="dual-card dual-card--biz"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-15%' }}
            transition={{ duration: 0.7, ease: [0.7, 0, 0.15, 1], delay: 0.1 }}
          >
            <div className="dual-card-eyebrow mono">for procurement leads</div>
            <h3 className="dual-card-title">
              Catch every relevant tender on day one of publication.
            </h3>
            <ul className="dual-card-list">
              <li>One key for the entire department  -  no per-seat counting.</li>
              <li>Watchlists: filter once, get a Slack/email/webhook ping when matches publish.</li>
              <li>Multi-jurisdiction coverage including sub-threshold registers.</li>
              <li>Minute-level freshness  -  hours ahead of nightly-bulk aggregators.</li>
            </ul>
            <div className="dual-card-stat">
              <div className="dual-stat-row">
                <span>In-house build · 1 FTE eng / yr</span>
                <span className="num">$220k+</span>
              </div>
              <div className="dual-stat-row">
                <span>Other aggregation services nightly bulk / yr</span>
                <span className="num">$15,000</span>
              </div>
              <div className="dual-stat-row dual-stat-row--ours">
                <span>tender-api Business / yr</span>
                <span className="num accent">$11,988</span>
              </div>
            </div>
            <Link to="/coverage" className="dual-card-foot">
              See the coverage matrix →
            </Link>
          </motion.article>
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
 * Coverage strip  -  flag pills, link to full matrix.
 * ================================================================== */
function CoverageStrip() {
  // Pinned to the 5 jurisdictions actually live in production today plus
  // a curated next-up shortlist. Source-of-truth: docs/coverage/portals.yaml.
  const live = ['ch', 'gb', 'us', 'ua'];
  const upNext = ['de', 'fr', 'it', 'es', 'nl', 'pl', 'se', 'no', 'fi', 'ie', 'at', 'cz', 'hu', 'sk', 'eu'];
  const codes = [...live, ...upNext];
  return (
    <section className="section section-coverage">
      <div className="container">
        <div className="coverage-head">
          <p className="eyebrow mono">
            <span className="dot" /> coverage today
          </p>
          <h2 className="section-title">
            5 jurisdictions live · more rolling out · sub-threshold registers committed
          </h2>
          <p className="section-lede">
            Every byte sourced from the official state portal  -  never third-party
            scrapers, never data-broker mirrors, never commercial aggregator
            re-sales. Auditable license trail per jurisdiction.
          </p>
        </div>
        <motion.div
          className="coverage-grid mono"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-10%' }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.025 } },
          }}
        >
          {codes.map((c) => (
            <motion.span
              key={c}
              variants={{
                hidden: { opacity: 0, y: 8 },
                show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
              }}
              className="coverage-pill"
            >
              <Icon
                icon={`flag:${c}-4x3`}
                inline
                style={{ fontSize: 14, marginRight: 6 }}
              />
              {c.toUpperCase()}
            </motion.span>
          ))}
        </motion.div>
        <div className="coverage-foot">
          <MagneticLink href="/coverage" variant="ghost">
            See the full coverage matrix
          </MagneticLink>
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
 * Anchor price  -  three-column comparison.
 * ================================================================== */
function AnchorPrice() {
  return (
    <section className="section section-anchor">
      <div className="container">
        <motion.div
          className="anchor-block"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-15%' }}
          transition={{ duration: 0.85, ease: [0.7, 0, 0.15, 1] }}
        >
          <p className="eyebrow mono">
            <span className="dot" /> price · vs the alternatives
          </p>
          <h2 className="anchor-title">What the others charge.</h2>
          <div className="anchor-row">
            <div className="anchor-col">
              <div className="anchor-name">Category-leading aggregator</div>
              <div className="anchor-num strike">$1,250</div>
              <div className="anchor-foot mono">/ mo · nightly bulk · CSV</div>
            </div>
            <div className="anchor-col">
              <div className="anchor-name">Single-jurisdiction vendor</div>
              <div className="anchor-num strike">$1,650</div>
              <div className="anchor-foot mono">/ mo · single jurisdiction · weekly</div>
            </div>
            <div className="anchor-col anchor-col--ours">
              <div className="anchor-name">tender-api · Business</div>
              <div className="anchor-num">$999</div>
              <div className="anchor-foot mono">/ mo · per institution · real-time · multi-jurisdiction</div>
            </div>
          </div>
          <div className="anchor-cta">
            <MagneticLink href="/signup" variant="primary">
              Start the trial  -  no card to view samples
            </MagneticLink>
            <p className="anchor-fine mono">
              $999 / mo · per legal entity · cancel from the customer portal · pay-per-record overage
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* =====================================================================
 * Trust grid  -  4 differentiators framed as proofs.
 * ================================================================== */
function TrustGrid() {
  const items = [
    {
      kicker: '01',
      title: 'Regulator-grade sources only',
      body:
        "TED, BOAMP, ANAC, KIMDIS, FTS, OGP, Vergabe  -  every byte from the official portal, never third-party scrapers, never data-broker mirrors. Each portal's license tag travels with every record.",
    },
    {
      kicker: '02',
      title: 'Minute-level freshness',
      body:
        "We poll every portal at its safe maximum, dedupe at the API edge, push via SSE. Hours ahead of nightly-bulk aggregators.",
    },
    {
      kicker: '03',
      title: 'Verbatim raw + normalised',
      body:
        'Two flavours per record. JSON normalised for the 90% case, eForms UBL XML / TED projection on /raw for the 10% where you need original bytes.',
    },
    {
      kicker: '04',
      title: 'Sub-threshold included',
      body:
        'German Vergabe-Plattform, Italian ANAC sub-threshold, Greek KIMDIS  -  the long tail commercial aggregators charge $15k/yr for. Same plan, same price.',
    },
  ];
  return (
    <section className="section section-trust">
      <div className="container">
        <p className="eyebrow mono">
          <span className="dot" /> why teams switch
        </p>
        <h2 className="section-title">
          Four hard claims  -  receipts in the API.
        </h2>
        <div className="trust-grid">
          {items.map((it, i) => (
            <motion.article
              key={it.kicker}
              className="trust-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.6, ease: [0.7, 0, 0.15, 1], delay: i * 0.06 }}
            >
              <span className="trust-kicker mono">{it.kicker}</span>
              <h3 className="trust-title">{it.title}</h3>
              <p className="trust-body">{it.body}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
 * FAQ  -  institutional buying questions.
 * ================================================================== */
function ObjectionFAQ({ items }) {
  return (
    <section className="section section-faq">
      <div className="container">
        <p className="eyebrow mono">
          <span className="dot" /> questions teams actually ask
        </p>
        <h2 className="section-title">Objections, answered.</h2>
        <div className="faq-list">
          {items.map((it, i) => (
            <motion.details
              key={i}
              className="faq-item"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10%' }}
              transition={{ duration: 0.5, ease: [0.7, 0, 0.15, 1], delay: i * 0.05 }}
            >
              <summary>{it.q}</summary>
              <p>{it.a}</p>
            </motion.details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
 * Final CTA
 * ================================================================== */
function FinalCTA() {
  return (
    <section className="section section-final">
      <div className="container final-block">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-20%' }}
          transition={{ duration: 0.85, ease: [0.7, 0, 0.15, 1] }}
        >
          <h2 className="final-title">
            $999 a month. <span className="accent">One key, one institution.</span>
          </h2>
          <p className="final-lede">
            Card-only billing via Stripe. No annual commitment, no per-seat counting,
            no surprise step-up. Cancel from the customer portal anytime. The first
            thousand records are free  -  you'll know if it works long before invoicing
            kicks in.
          </p>
          <div className="final-cta-row">
            <MagneticLink href="/signup" variant="primary">
              Get an institution key
            </MagneticLink>
            <MagneticLink href="/pricing" variant="ghost">
              Compare plans
            </MagneticLink>
          </div>
          <p className="final-meta mono">
            no card to view samples · cancel anytime · regulator-grade sources only
          </p>
        </motion.div>
      </div>
    </section>
  );
}
